import * as B from '@blueprintjs/core';
import * as S from '@blueprintjs/select';
import React from 'react';

import {GeometryFileUploader} from 'app/components/ManagePortfolio/GeometryFileUploader';
import {HoverProperty} from 'app/modules/Remote/FeatureCollection';

import {NumberedFeatureCollection} from './ManageFeaturesProvider';
import cs from './styles.module.scss';

import {ManagePropertiesMode} from '.';

export interface Props {
  isConvertingFile: boolean;
  problems: string[] | null;
  filename: string | null;
  onFilenameChange: (file: File) => void;
  uploadedFeatureCollection: NumberedFeatureCollection | null;
  nameField: string | null;
  setNameField: React.Dispatch<React.SetStateAction<string | null>>;
  setPartNameField: React.Dispatch<React.SetStateAction<string | null>>;
  mode: ManagePropertiesMode;
  hoverProperties: HoverProperty[];
  setHoverProperties: React.Dispatch<React.SetStateAction<HoverProperty[]>>;
}

const ManagePropertiesFileUploader: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  isConvertingFile,
  problems,
  filename,
  uploadedFeatureCollection,
  nameField,
  setNameField,
  setPartNameField,
  onFilenameChange,
  mode,
  hoverProperties,
  setHoverProperties,
}) => {
  const features = uploadedFeatureCollection?.features;
  const showNameSelect = !problems && features && features.length > 0;

  // Hide backend-derived annotations (e.g. _UPSTREAM_ACRES, __UPSTREAM_PROBLEMS,
  // __UPSTREAM_WARNINGS) from the user-facing dropdowns.
  const propertyKeys = features?.[0]?.properties
    ? Object.keys(features[0].properties).filter((k) => !/^_+UPSTREAM_/.test(k))
    : [];

  const selectedKeys = hoverProperties.map((h) => h.key);

  return (
    <div className={cs.formFields}>
      <GeometryFileUploader
        isConvertingFile={isConvertingFile}
        conversionErrors={problems}
        filename={filename}
        onFilenameChange={onFilenameChange}
        hasSelection={!!uploadedFeatureCollection}
        supportDocUrl={
          'https://lens.tech/support/article/147-preparing-geospatial-data-for-lens'
        }
      />
      {showNameSelect && (mode === 'overlay' || mode === 'updateOverlay') && (
        <div className={cs.nameFieldSelect}>
          <label className={cs.fieldLabel}>Hover attribute(s)</label>
          <S.MultiSelect<string>
            fill
            items={propertyKeys}
            selectedItems={selectedKeys}
            onItemSelect={(key) => {
              setHoverProperties((prev) =>
                prev.some((h) => h.key === key)
                  ? prev.filter((h) => h.key !== key)
                  : [...prev, {key, label: key}]
              );
            }}
            onRemove={(key) => {
              setHoverProperties((prev) => prev.filter((h) => h.key !== key));
            }}
            itemRenderer={(key, {handleClick, modifiers}) => (
              <B.MenuItem
                key={key}
                text={key}
                onClick={handleClick}
                active={modifiers.active}
                selected={selectedKeys.includes(key)}
                disabled={modifiers.disabled}
                roleStructure="listoption"
              />
            )}
            tagRenderer={(key) => key}
            itemPredicate={(query, key) => key.toLowerCase().includes(query.toLowerCase())}
            noResults={
              <B.MenuItem disabled text="No matching attributes." roleStructure="listoption" />
            }
            popoverProps={{minimal: true}}
            placeholder="Select attributes (preview on map)..."
          />
        </div>
      )}
      {showNameSelect && mode !== 'overlay' && mode !== 'updateOverlay' && (
        <div className={cs.nameFieldSelect}>
          <label className={cs.fieldLabel}>Name attribute</label>
          <B.HTMLSelect
            fill
            onChange={(ev) => {
              setNameField(ev.currentTarget.value);
              setPartNameField(null);
            }}
            value={nameField ?? 'none'}
          >
            <option disabled value="none">
              Select…
            </option>
            {propertyKeys.map((key) => (
              <option key={key} value={key}>
                {key}
              </option>
            ))}
          </B.HTMLSelect>
        </div>
      )}
    </div>
  );
};

export default ManagePropertiesFileUploader;
